// Maps the three settings DTOs onto SettingRow items (architecture sprint-11 §9.4, §9.5). Pure.
import { SETTINGS_CATALOGUE, isSettingKey, type SettingGroup } from '@shared/settings-catalogue';
import type { GlobalSetting, ProjectSetting, TemplateValueItem } from '@/types/settings';
import type { SettingRowItem } from './SettingRow';

export const BADGE_SET_HERE = 'Set here';
export const BADGE_INHERITED_GLOBAL = 'Inherited (global)';
export const BADGE_INHERITED_DEFAULT = 'Inherited (default)';
export const BADGE_DEFAULT = 'Default';
export const BADGE_TEMPLATE_SET = 'Set in template';
export const BADGE_TEMPLATE_UNSET = 'Not set (inherits)';
export const ADMIN_ONLY_LABEL = 'Admin only';
const WORKWEEK_HINT = 'Drives Missing entries.';

export interface GroupedItem {
  group: SettingGroup;
  item: SettingRowItem;
}

export function globalItem(s: GlobalSetting): GroupedItem {
  return {
    group: s.group,
    item: {
      key: s.key,
      label: s.label,
      description: s.key === 'workweek.days' ? undefined : s.description,
      hint: s.key === 'workweek.days' ? WORKWEEK_HINT : undefined,
      kind: s.kind,
      value: s.value,
      badge: s.source === 'global' ? BADGE_SET_HERE : BADGE_DEFAULT,
      canEdit: true,
      canReset: s.source === 'global',
    },
  };
}

/** Sprint 13 (§5.2): a key forced off by shift mode. Every source mapper handles every source (sprint 11 learning). */
export const BADGE_SHIFT_MODE = 'Off (shift mode)';

export function projectItem(s: ProjectSetting, viewer: 'admin' | 'supervisor'): GroupedItem {
  const badge =
    s.source === 'shift-mode'
      ? BADGE_SHIFT_MODE
      : s.source === 'project'
        ? BADGE_SET_HERE
        : s.source === 'global'
          ? BADGE_INHERITED_GLOBAL
          : BADGE_INHERITED_DEFAULT;
  return {
    group: s.group,
    item: {
      key: s.key,
      label: s.label,
      description: s.description,
      kind: s.kind,
      value: s.value,
      badge,
      canEdit: s.canEdit,
      lockedLabel: viewer === 'supervisor' && !s.canEdit && s.source !== 'shift-mode' ? ADMIN_ONLY_LABEL : undefined,
      mode: s.mode,
      canChooseMode: s.canChooseMode,
      canReset: s.canReset,
    },
  };
}

export function templateItem(t: TemplateValueItem): GroupedItem {
  const def = isSettingKey(t.key) ? SETTINGS_CATALOGUE[t.key] : null;
  const set = t.value !== null;
  return {
    group: t.group,
    item: {
      key: t.key,
      label: t.label,
      description: def?.description,
      kind: t.kind,
      value: set ? t.value : (def?.default ?? null),
      badge: set ? BADGE_TEMPLATE_SET : BADGE_TEMPLATE_UNSET,
      canEdit: true,
      mode: t.mode,
      canChooseMode: t.canChooseMode && set,
      canReset: set,
    },
  };
}

/** Sprint 14 (§8.7): the hint on alarm rows while shift mode is off (the keys are inert, B-8). */
export const NEEDS_SHIFT_MODE = 'Needs shift mode';
export const WINDOW_BOTH_OR_NEITHER = 'Set both times, or neither';

/**
 * Sprint 14 (§8.7): a project's alarm rows are disabled with "Needs shift mode" while shift.enabled resolves false,
 * and the window pair warns when exactly one end is set. Pure: takes and returns the grouped rows.
 */
export function withAlarmRules(rows: GroupedItem[]): GroupedItem[] {
  const shiftOn = rows.find((r) => r.item.key === 'shift.enabled')?.item.value === true;
  const start = rows.find((r) => r.item.key === 'alarm.windowStart')?.item.value ?? null;
  const end = rows.find((r) => r.item.key === 'alarm.windowEnd')?.item.value ?? null;
  const oneEnd = (start === null) !== (end === null);
  return rows.map((r) => {
    if (r.group !== 'alarms') return r;
    let item = r.item;
    if (!shiftOn) item = { ...item, canEdit: false, canChooseMode: false, canReset: false, hint: NEEDS_SHIFT_MODE };
    else if (item.key === 'alarm.windowEnd' && oneEnd) item = { ...item, hint: WINDOW_BOTH_OR_NEITHER };
    return { ...r, item };
  });
}
